import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { graphql } from 'gatsby'
import {
  Container,
  Section,
  SectionTitle,
  Inline,
  ListItem,
} from 'chaoskit/src/components'
import { gradient, link, text } from 'chaoskit/src/assets/styles/utility'
import { InstantSearch, Configure } from 'react-instantsearch-dom'

import {
  HomeHeroGrid,
  HomeHeroGridImage,
  HomeHeroGridContent,
} from '~partials/home/Hero'
import BaseLayout from '~layouts/BaseLayout'
import Hero, { HeroTitle, HeroTitleSub } from '~partials/Hero'
import Data from '~components/Data'
import { IntroBoxUI } from '~partials/global/IntroBox'
import { CustomInfiniteHits } from '~partials/search/algolia-global'
import { Hit } from '~partials/search/algolia-resources'
import heroMobile from '~media/podcast/hero-mobile.svg'
import hero from '~media/podcast/hero.svg'
import apple from '~media/podcast/apple-podcasts.svg'
import overcast from '~media/podcast/overcast.svg'
import spotify from '~media/podcast/spotify.svg'
import pocket from '~media/podcast/pocket-casts.svg'
import castro from '~media/podcast/castro.svg'
import stitcher from '~media/podcast/stitcher.svg'
import rss from '~media/podcast/rss.svg'
import HelmetSEO from '~partials/global/HelmetSEO'
import Image from '~components/Image'
import TextLink from '~components/TextLink'
import useAlgoliaSearchClient from '~hooks/useAlgoliaSearchClient'

const subscribeMediums = [
  {
    name: 'Apple Podcasts',
    icon: apple,
    url:
      'https://podcasts.apple.com/us/podcast/break-things-on-purpose-btop/id1547501894',
  },
  {
    name: 'Overcast',
    icon: overcast,
    url: 'https://overcast.fm/itunes1547501894',
  },
  {
    name: 'Spotify',
    icon: spotify,
    url: 'https://open.spotify.com/show/52gvipIrcEF06dWYzbtlZd',
  },
  {
    name: 'Pocket Casts',
    icon: pocket,
    url: 'https://pca.st/itunes/1547501894',
  },
  {
    name: 'Castro',
    icon: castro,
    url: 'https://castro.fm/itunes/1547501894',
  },
  {
    name: 'Stitcher',
    icon: stitcher,
    url: 'https://www.stitcher.com/show/break-things-on-purpose',
  },
  {
    name: 'RSS Feed',
    icon: rss,
    url:
      'https://feeds.transistor.fm/break-things-on-purpose-7790d923-3197-451b-8643-92f6f2d650c2',
  },
]

const Podcast = ({
  data: {
    file: {
      childMdx: { frontmatter: pageData },
    },
  },
}) => {
  const theme = useTheme()

  const searchClient = useAlgoliaSearchClient((state) => state.searchClient)

  return (
    <BaseLayout>
      <HelmetSEO
        title={pageData.title}
        description={pageData.description}
        image={pageData.socialMediaImage}
      />
      <Hero css={{ ...gradient.dark(theme) }}>
        <HomeHeroGrid>
          <HomeHeroGridImage
            css={{
              [theme.mq.medium]: {
                alignSelf: 'end',

                img: {
                  height: 700,
                  marginBottom: '-31%', // To line-up bottom of mic with `<IntroBox />`
                },
              },

              [theme.mq.small__max]: {
                img: {
                  maxHeight: 400,
                },
              },
            }}
          >
            <Image
              css={{
                [theme.mq.small__max]: {
                  display: 'none',
                },
              }}
              src={hero}
              alt=""
            />
            <Image
              css={{
                [theme.mq.medium]: {
                  display: 'none',
                },

                transform: 'translateX(12.5%)', // Offset to align mic
              }}
              src={heroMobile}
              alt=""
            />
          </HomeHeroGridImage>
          <HomeHeroGridContent>
            <HeroTitle size="large">
              <Data>{pageData.hero.heading}</Data>
            </HeroTitle>
            <HeroTitleSub>
              <Data>{pageData.hero.blurb}</Data>
            </HeroTitleSub>
          </HomeHeroGridContent>
        </HomeHeroGrid>
      </Hero>
      <main css={{ position: 'relative', zIndex: 1 }}>
        <IntroBoxUI
          shadowDirection="left"
          css={{
            '.IntroBox__Inner': {
              padding: `${theme.space.base}px !important`,
            },
          }}
        >
          <iframe
            title="Break Things On Purpose"
            src="https://share.transistor.fm/e/break-things-on-purpose-7790d923-3197-451b-8643-92f6f2d650c2/latest"
            frameBorder="0"
            scrolling="no"
            css={{
              width: '100%',
              height: 180,
            }}
          />
        </IntroBoxUI>
        <Section css={{ paddingTop: `${theme.space.large}px !important` }}>
          <Container size="small">
            <Inline size="base" css={{ justifyContent: 'center' }}>
              {subscribeMediums.map((medium) => (
                <ListItem key={medium.name}>
                  <TextLink
                    href={medium.url}
                    css={{
                      ...link.reset(theme),
                      fontWeight: theme.fontWeight.bold,
                      display: 'grid',
                      alignItems: 'center',
                      gap: theme.space.small,
                      gridTemplateColumns: '20px 1fr',
                      border: `1px solid ${theme.fontColor.base}`,
                      borderRadius: theme.borderRadius.base,
                      paddingTop: theme.space.xsmall,
                      paddingBottom: theme.space.xsmall,
                      paddingLeft: theme.space.base,
                      paddingRight: theme.space.base,
                      minHeight: theme.height.base,

                      '&:hover, &:focus': {
                        img: {
                          transform: 'scale(1.1)',
                        },
                      },
                    }}
                  >
                    <Image
                      src={medium.icon}
                      alt={medium.name}
                      css={{
                        transition: `transform ${theme.timing.base} ${theme.transition.bounce}`,
                        transformOrigin: 'center center',
                      }}
                    />
                    <div>
                      <div
                        css={{
                          ...text.expanded(theme),
                          ...theme.text['2xsmall'],
                        }}
                      >
                        {medium.name !== 'RSS Feed' ? 'Listen on' : 'Subscribe'}
                      </div>
                      <div css={{ fontWeight: theme.fontWeight.bold }}>
                        {medium.name}
                      </div>
                    </div>
                  </TextLink>
                </ListItem>
              ))}
            </Inline>
          </Container>
        </Section>
        <Section>
          <Container>
            <SectionTitle
              as="h2"
              title={<Data>{pageData.episodes.heading}</Data>}
            />
            <InstantSearch indexName="resources" searchClient={searchClient}>
              <Configure filters="topics:Podcast" hitsPerPage="3" />
              <CustomInfiniteHits
                hitTemplate={Hit}
                customCss={{
                  [theme.mq.small]: {
                    gridTemplateColumns: 'repeat(2, 1fr)',
                  },

                  [theme.mq.medium]: {
                    gridTemplateColumns: 'repeat(3, 1fr)',
                  },
                }}
              />
            </InstantSearch>
          </Container>
        </Section>
      </main>
    </BaseLayout>
  )
}

Podcast.propTypes = {
  data: PropTypes.object.isRequired,
}

export default Podcast

export const pageQuery = graphql`
  query PodcastPageData {
    file(name: { eq: "podcast" }, absolutePath: { regex: "/src/data/" }) {
      childMdx {
        frontmatter {
          title
          description
          socialMediaImage
          hero {
            heading
            blurb
          }
          episodes {
            heading
          }
        }
      }
    }
  }
`
